import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBar,
  ChartLineUp,
  MegaphoneSimple,
  Moon,
  PaperPlaneTilt,
  Pulse,
  Sun,
} from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { Badge, Spinner } from '@/components/ui-kit';
import { useInstances } from '@/context/InstanceContext';

const DAILY_VERSES = [
  { ref: 'Salmos 118:24', text: 'Este é o dia que fez o Senhor; regozijemo-nos e alegremo-nos nele.' },
  { ref: 'Provérbios 3:5', text: 'Confia no Senhor de todo o teu coração, e não te estribes no teu próprio entendimento.' },
  { ref: 'Filipenses 4:13', text: 'Posso todas as coisas naquele que me fortalece.' },
  { ref: 'Salmos 23:1', text: 'O Senhor é o meu pastor; nada me faltará.' },
  { ref: 'Isaías 41:10', text: 'Não temas, porque eu sou contigo; não te assombres, porque eu sou teu Deus.' },
  { ref: 'Romanos 8:28', text: 'Todas as coisas contribuem juntamente para o bem daqueles que amam a Deus.' },
  { ref: 'Mateus 6:33', text: 'Buscai primeiro o reino de Deus, e a sua justiça, e todas estas coisas vos serão acrescentadas.' },
  { ref: 'Jeremias 29:11', text: 'Eu bem sei os pensamentos que penso de vós, pensamentos de paz, e não de mal.' },
  { ref: 'Salmos 46:1', text: 'Deus é o nosso refúgio e fortaleza, socorro bem presente na angústia.' },
  { ref: 'Josué 1:9', text: 'Esforça-te, e tem bom ânimo; não temas, nem te espantes.' },
  { ref: 'João 14:27', text: 'Deixo-vos a paz, a minha paz vos dou; não se turbe o vosso coração.' },
  { ref: '2 Coríntios 5:7', text: 'Porque andamos por fé, e não por vista.' },
  { ref: 'Salmos 37:5', text: 'Entrega o teu caminho ao Senhor; confia nele, e ele o fará.' },
  { ref: 'Lamentações 3:23', text: 'Novas são cada manhã; grande é a tua fidelidade.' },
];

function verseOfTheDay() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const day = Math.floor((now - start) / 86400000);
  return DAILY_VERSES[day % DAILY_VERSES.length];
}

export default function DashboardPage({ theme, onToggleTheme }) {
  const { instances } = useInstances();
  const [data, setData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      try {
        const [d, campaignList] = await Promise.all([
          api.dashboard(),
          api.listCampaigns().catch(() => []),
        ]);
        if (alive) setData(d);
        if (alive) setCampaigns(campaignList);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetch();
    const t = setInterval(fetch, 8000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const connectedCount = instances.filter((i) => i.connectionStatus === 'open').length;
  const activeInstances = instances.filter((i) => i.connectionStatus === 'open');
  const activeCampaigns = campaigns.filter((c) => c.status !== 'archived');
  const greetingName = activeInstances[0]?.profileName || activeInstances[0]?.instanceName || 'Willian';
  const dailyVerse = verseOfTheDay();

  return (
    <div data-testid="page-dashboard">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="h-px w-7" style={{ background: 'var(--s1-accent)' }} />
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: 'var(--s1-accent-text)' }}>
            Visão geral
          </span>
        </div>
        <button
          type="button"
          onClick={onToggleTheme}
          className="flex items-center gap-3 rounded-full border px-4 py-2"
          style={{
            background: 'var(--s1-card)',
            borderColor: 'var(--s1-card-border)',
            color: 'var(--s1-text-2)',
          }}
        >
          <span className="text-[11px] font-semibold uppercase tracking-[0.1em]">
            {theme === 'dark' ? 'Modo escuro' : 'Modo claro'}
          </span>
          <span className="grid h-8 w-8 place-items-center rounded-full" style={{ background: 'var(--s1-accent-soft)', color: 'var(--s1-accent-text)' }}>
            {theme === 'dark' ? <Moon size={15} /> : <Sun size={16} />}
          </span>
        </button>
      </div>

      <h1 className="mt-5 max-w-4xl font-display text-[42px] font-normal leading-[1.05]" style={{ color: 'var(--s1-text)' }}>
        Bom dia, {greetingName}
      </h1>
      <p className="mt-3 max-w-xl text-[15px] leading-relaxed" style={{ color: 'var(--s1-text-2)' }}>
        Estado operacional em tempo real do seu disparador WhatsApp. As métricas atualizam a cada 8 segundos.
      </p>

      {loading && !data ? (
        <div className="mt-8 flex items-center gap-2" style={{ color: 'var(--s1-text-2)' }}>
          <Spinner /> carregando métricas...
        </div>
      ) : (
        <>
          <div className="mt-9 grid grid-cols-2 gap-4 xl:grid-cols-5">
            <MetricCard label="Instâncias conectadas" value={connectedCount} total={instances.length} icon={Pulse} testId="metric-instances" />
            <MetricCard label="Campanhas ativas" value={data?.campaigns || 0} icon={MegaphoneSimple} testId="metric-groups" />
            <MetricCard label="Mensagens enviadas" value={data?.sent || 0} icon={PaperPlaneTilt} testId="metric-sent" />
            <MetricCard label="Taxa de entrega" value={`${data?.deliveryRate ?? 0}%`} icon={ChartLineUp} testId="metric-delivery" />
            <MetricCard label="Hits do funil" value={data?.funnelHits || 0} icon={ChartBar} testId="metric-funnel-hits" />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1.45fr]">
            <section className="s1-card rounded-[var(--s1-radius-card)] border p-6" style={{ background: 'var(--s1-card)', borderColor: 'var(--s1-card-border)' }}>
              <h2 className="font-display text-[22px] font-medium leading-tight" style={{ color: 'var(--s1-text)' }}>Instâncias ativas</h2>
              <p className="mt-2 text-[12.5px] font-medium" style={{ color: 'var(--s1-muted)' }}>Números conectados agora</p>
              {activeInstances.length === 0 ? (
                <div className="mt-5 rounded-[var(--s1-radius-inner)] border px-4 py-6 text-center text-sm" style={{ borderColor: 'var(--s1-inner-border)', color: 'var(--s1-muted)' }}>
                  Nenhuma instância conectada.
                </div>
              ) : (
                <div className="mt-5 flex flex-col gap-3">
                  {activeInstances.slice(0, 5).map((inst) => (
                    <div key={inst.instanceName} className="s1-inner flex items-center justify-between rounded-[var(--s1-radius-inner)] border px-4 py-4" style={{ background: 'var(--s1-inner)', borderColor: 'var(--s1-inner-border)' }}>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold" style={{ color: 'var(--s1-text)' }}>{inst.profileName || inst.instanceName}</p>
                        <p className="mt-1 truncate font-mono text-xs" style={{ color: 'var(--s1-muted)' }}>{inst.ownerJid ? inst.ownerJid.replace('@s.whatsapp.net', '') : inst.instanceName}</p>
                      </div>
                      <Badge className="border-0" style={{ background: 'var(--s1-accent-soft)', color: 'var(--s1-accent-text)' }}>open</Badge>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="s1-card rounded-[var(--s1-radius-card)] border p-6" style={{ background: 'var(--s1-card)', borderColor: 'var(--s1-card-border)' }}>
              <h2 className="font-display text-[22px] font-medium leading-tight" style={{ color: 'var(--s1-text)' }}>Campanhas ativas</h2>
              <p className="mt-2 text-[12.5px] font-medium" style={{ color: 'var(--s1-muted)' }}>Operações disponíveis para acompanhar</p>
              {activeCampaigns.length === 0 ? (
                <div className="mt-5 rounded-[var(--s1-radius-inner)] border px-4 py-6 text-center text-sm" style={{ borderColor: 'var(--s1-inner-border)', color: 'var(--s1-muted)' }}>
                  Nenhuma campanha ativa.
                </div>
              ) : (
                <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {activeCampaigns.slice(0, 6).map((campaign) => (
                    <Link
                      key={campaign.id}
                      to={`/campaigns/${campaign.id}`}
                      className="s1-inner rounded-[var(--s1-radius-inner)] border px-5 py-4 transition-colors hover:opacity-90"
                      style={{ background: 'var(--s1-inner)', borderColor: 'var(--s1-inner-border)' }}
                    >
                      <p className="truncate text-[15px] font-semibold" style={{ color: 'var(--s1-text)' }}>{campaign.name}</p>
                      <p className="mt-2 text-[12.5px] font-medium" style={{ color: 'var(--s1-text-2)' }}>
                        {campaign.groupsCount || 0} grupos · {campaign.instanceName || 'sem instância'}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </div>

          <section className="mt-6 flex items-start justify-between gap-6 rounded-[var(--s1-radius-card)] border px-8 py-7" style={{ background: 'var(--s1-devotional)', borderColor: 'var(--s1-devotional-border)' }}>
            <div>
              <p className="brand-script text-[12px] not-italic uppercase tracking-[0.2em]" style={{ fontFamily: 'var(--s1-font-body)' }}>Devocional do dia</p>
              <p className="mt-4 max-w-3xl font-display text-2xl italic leading-snug" style={{ color: 'var(--s1-devotional-text)' }}>
                “{dailyVerse.text}”
              </p>
              <p className="mt-3 text-[12.5px] font-semibold" style={{ color: 'var(--s1-devotional-cite)' }}>{dailyVerse.ref}</p>
            </div>
            <span className="rounded-[8px] border px-3 py-2 text-[9.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--s1-devotional-cite)', borderColor: 'var(--s1-devotional-border)' }}>
              Diário
            </span>
          </section>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value, total, icon: Icon, testId }) {
  return (
    <section
      className="s1-card rounded-[var(--s1-radius-metric)] border px-5 py-5"
      style={{ background: 'var(--s1-card)', borderColor: 'var(--s1-card-border)' }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-4">
        <p className="max-w-[120px] text-[10px] font-semibold uppercase leading-snug tracking-[0.13em]" style={{ color: 'var(--s1-muted)' }}>
          {label}
        </p>
        <Icon size={17} style={{ color: 'var(--s1-accent)' }} />
      </div>
      <p className="mt-5 font-display text-[38px] font-medium leading-none" style={{ color: 'var(--s1-text)' }}>
        {value}
        {total !== undefined && <span className="text-[22px] font-normal" style={{ color: 'var(--s1-faint)' }}> / {total}</span>}
      </p>
    </section>
  );
}
